import { RunStatusDto } from "@store/services/runService"

export type getRunsQueryParams = {
  pagination__last__key: string
  pagination__limit: number
}

export type ModelRunPagination = {
  pagination__last__key?: string
  pagination__last__node_key?: string
  pagination__last__timestep?: number
  pagination__limit?: number
}

export type getRunNodeYearsParams = {
  key: string
} & ModelRunPagination

export type getRunAggregates = {
  key: string
}

export type getRunAggregatesNodeAggregate = {
  keys: string[]
  node: string
  aggregateFunctions: string[]
}

export type getRunNodeYearsFilterNodeParams = {
  key: string
  node: string
} & ModelRunPagination

export type RunDto = {
  key: string
  run_name: string | null
  creator_email: string
  num_samples: number
  seed: number
  version: string
  owner_group_key: string
  model_key: string
  creation_date: string
  status: string
  extra?: {
    message: string
    // TODO in the future remove 'message' -- errors should be returned as list of string or undefined. Added as temp fix.
    traceback?: { message: string } | string | string[] | undefined
    task?: string
    file?: string
    can_user_download_results?: boolean
  }
  last_updated: string
  family: string
  simulation_frequency: string
  yearly_rolling_start_month: string
  run_group_key: string
  base_graph_key: string
  user_graph_key: string // empty string if it is not user graph
  post_validate: boolean
  linked_runs?: { [graphKey: string]: string } // {[graphKey]: runGroupId}
}

export type RunNodeTimestepDto<T extends number | string = number> = {
  key: string
  node_key: string
  timestep: number
  data: T[]
}

export type LastKeyRunNodeYearResponse = {
  key: string
  node_key: string
  year: number
}

export type RunNodeTimestepResponseDto<T extends number | string = number> = {
  items: RunNodeTimestepDto<T>[]
  total: number
  total_all_pages: number
  last_key: LastKeyRunNodeYearResponse
  limit: number | null
}

export type RunAggregatesResponseDto = {
  timesteps: string[]
  data: string[]
}

export type RunAggregatesGroupResponseDto = {
  // key is run key
  [key: string]: {
    // key is aggregate function e.g. "mean"
    [key: string]: {
      timesteps: string[]
      data: string[]
    }
  }
}

export type RunsNodesResults = {
  results: RunResults
}

export type RunsNodesHistoricResults = {
  results: RunResultsHistoric
}

export type RunResults<TData extends string | number = number> = {
  [key: RunDto["key"]]: NodeResults<TData>
}

export type RunResultsHistoric = {
  [key: string]: NodeResultsHistoric | {}
}

export type NodeResults<TData extends string | number = number> = {
  [key: string]: NodeResult<TData>[] // key here is parsed node label e.g. "WLD_aluminium_price"
}

export interface NodeResultsHistoricNames {
  // { <timestep>: <node label> } eg. for lag = 1 { 20240101: "WLD aluminium price lag1", 20250101: "WLD aluminium price", ... }
  names: Record<string, string>
  unitFormat?: string // added later on, won't be there for sampling results made before this was added
  historic?: {
    Date: string
    Mnem: string
    Value: string
  }[]
}

export interface NodeResultsHistoricValue extends NodeResultsHistoricNames {
  timesteps?: { [key: string]: number[] | string[] }
}

export interface NodeResultsHistoric {
  [key: string]: NodeResultsHistoricValue
}

export interface RunResultsHistoricAggregate {
  results: RunResultsAggregate
}

export interface RunResultsAggregate {
  [key: string]: NodeResultsAggregate // key is run uuid
}

export type AggregateFunctionName =
  | "avg"
  | "d3_deviation"
  | "max"
  | "mean"
  | "median"
  | "min"
  | "quantile_10th"
  | "quantile_25th"
  | "quantile_50th"
  | "quantile_5th"
  | "quantile_75th"
  | "quantile_90th"
  | "quantile_95th"
  | "std"

export interface CategoricalNodeResultsAggregateValue extends NodeResultsHistoricNames {
  data_type: "categorical"
  category_count: Record<string, Record<string, string>> // e.g. { 20240101: { maybe: 400, no: 300, yes: 300 }, ...}
  timesteps: Record<string, NodeAggregatedResults> // key is a timestep e.g. "20231201"
}

export interface RawNodeResultsAggregateValue extends NodeResultsHistoricNames {
  data_type: "result"
  category_count: {}
  timesteps: Record<string, NodeAggregatedResults> // key is a timestep e.g. "20231201"
}

export type NodeResultsAggregateValue =
  | CategoricalNodeResultsAggregateValue
  | RawNodeResultsAggregateValue

export type NodeAggregatedResults = {
  [key in AggregateFunctionName]: number
}

export interface NodeResultsAggregate {
  [key: string]: NodeResultsAggregateValue // key is node's sample label
}

export type NodeResult<TData extends string | number = number> = {
  data: TData[]
  key: string // run key
  node_key: string
  timestep: number
  unit_format: string
}

export type RunResponseDto = {
  items: RunDto[]
  total: number
  last_key: { key: string }
  limit: number
  total_all_pages: number
}

export type RunRecentItemDto = {
  key: string
  createDate: string
  progressLastUpdate: string
  status: string
  filter_by_user: boolean
}

export type PostRunGroupParams = {
  base_graph_key: string
  description: string
  end_date: string
  scenarios: string[]
  contract_key?: string
  run_name: string
  seed: number
  number_of_samplings: number
  graph_file_blob?: Blob
  validate: boolean
  sampling_engine: "EVIEWS"
}

export enum RunGroupStatus {
  RUNNING = 0,
  DONE = 1,
  ERROR = 2,
}

export type RunGroupResponse = {
  items: RunGroup[]
}

export type ConditionedResultErrorResponse = {
  error: string
  conditioned: Pick<ConditionedResult, "given" | "statement">
  unconditioned: Pick<UnconditionedResult, "statement">
}

export type ConditionedResultResponse = {
  blurb: string
  conditioned: ConditionedResult
  unconditioned: UnconditionedResult
}

export type ConditionedResult = {
  statement: string
  given: string
  samples: number[]
  count: number
  space: number
  value: number
}

export type UnconditionedResult = Omit<ConditionedResult, "given">

export type RunGroup = {
  key: string
  base_graph_key: string
  last_updated: string //datetime
  creation_date: string //datetime
  creator_email: string
  description: string
  family: string
  owner_group_key: string
  run_group_name: string
  status: RunGroupStatus
  runs: {
    [key: string]: ScenarioRun // key is run key
  }
}

export type ScenarioRun = {
  scenario_name: string
  status: RunStatusDto
  model_key: string
}
